import { useFormContext } from "react-hook-form";
import { FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Building2, MapPin, Clock, DollarSign, Users, Calendar, CheckCircle2, AlertCircle } from "lucide-react";

const officeLocations = {
  melbourne: {
    name: "Melbourne Office",
    address: "Unit 41/2 Fastline Rd, Truganina",
    hours: "9AM - 5PM",
    parking: "Free parking available",
  },
  launceston: {
    name: "Launceston Office",
    address: "Office 2, 187 Brisbane St",
    hours: "9AM - 5PM",
    parking: "Street parking available",
  },
};

export function ServiceSelectionSection() {
  const form = useFormContext();

  return (
    <div className="space-y-6 animate-fade-up">
      <Card>
        <CardHeader>
          <CardTitle>Select Service Type</CardTitle>
          <CardDescription>
            Choose between online submission or office visit
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormField
            control={form.control}
            name="service_type"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className="grid gap-4 md:grid-cols-2"
                  >
                    {/* Online Service Option */}
                    <Label
                      htmlFor="online"
                      className={`group relative cursor-pointer rounded-lg border-2 p-4 hover:bg-accent ${
                        field.value === "online" ? "border-[#C4A962] dark:border-[#9b87f5] bg-[#C4A962]/5 dark:bg-[#9b87f5]/5" : "border-muted"
                      }`}
                    >
                      <RadioGroupItem
                        value="online"
                        id="online"
                        className="absolute right-4 top-4"
                      />
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-5 w-5 text-[#C4A962] dark:text-[#9b87f5]" />
                          <span className="font-semibold">Online Service</span>
                        </div>
                        <Badge variant="secondary" className="mb-2">
                          $90 + GST
                        </Badge>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4" />
                            Convenient online submission
                          </li>
                          <li className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            24/7 availability
                          </li>
                        </ul>
                      </div>
                    </Label>

                    {/* Office Visit Option */}
                    <Label
                      htmlFor="office"
                      className={`group relative cursor-pointer rounded-lg border-2 p-4 hover:bg-accent ${
                        field.value === "office" ? "border-[#C4A962] dark:border-[#9b87f5] bg-[#C4A962]/5 dark:bg-[#9b87f5]/5" : "border-muted"
                      }`}
                    >
                      <RadioGroupItem
                        value="office"
                        id="office"
                        className="absolute right-4 top-4"
                      />
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <Users className="h-5 w-5 text-[#C4A962] dark:text-[#9b87f5]" />
                          <span className="font-semibold">Office Visit</span>
                        </div>
                        <Badge variant="secondary" className="mb-2">
                          $140 + GST
                        </Badge>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4" />
                            Face-to-face consultation
                          </li>
                          <li className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Flexible appointment times
                          </li>
                        </ul>
                      </div>
                    </Label>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      {form.watch("service_type") === "office" && (
        <Card>
          <CardHeader>
            <CardTitle>Select Office Location</CardTitle>
            <CardDescription>
              Choose your preferred office for the consultation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="preferred_office"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="grid gap-4 md:grid-cols-2"
                    >
                      {Object.entries(officeLocations).map(([key, office]) => (
                        <Label
                          key={key}
                          htmlFor={key}
                          className={`group relative cursor-pointer rounded-lg border-2 p-4 hover:bg-accent ${
                            field.value === key ? "border-primary bg-primary/5" : "border-muted"
                          }`}
                        >
                          <RadioGroupItem
                            value={key}
                            id={key}
                            className="absolute right-4 top-4"
                          />
                          <div className="space-y-4">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-5 w-5 text-primary" />
                              <span className="font-semibold">{office.name}</span>
                            </div>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                              <li className="flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                {office.address}
                              </li>
                              <li className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                {office.hours}
                              </li>
                              <li className="flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4" />
                                {office.parking}
                              </li>
                            </ul>
                          </div>
                        </Label>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>
      )}

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Need Help?</AlertTitle>
        <AlertDescription>
          Contact our friendly team:
          <div className="mt-2 space-y-1">
            <p>📞 1300 123 456</p>
            <p>✉️ support@numberz.com.au</p>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}